import { useState, useEffect } from 'react';
import { Podcast } from '../../types/index';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { slugify } from '../../lib/utils';
import { getLatestEpisodesByPodcastId } from '../../services/supabase/episodes';

interface PodcastCardProps {
  podcast: Podcast;
  isAuthenticated: boolean;
}

const ROTATION_INTERVAL = 2000; // 2 seconds
const MAX_EPISODES = 4; // Number of latest episodes to show
const STATIC_IMAGE_COUNT = 5; // Number of static images to try as fallback

/**
 * Convert YouTube thumbnail URL to medium quality (mqdefault).
 * Reduces file size from ~60KB to ~10KB while maintaining decent quality.
 */
function convertToMediumThumbnail(url: string): string {
  // Replace maxresdefault or any quality indicator with mqdefault
  return url.replace(/\/[^/]+\.webp$/, '/mqdefault.webp')
    .replace(/\/[^/]+\.jpg$/, '/mqdefault.jpg')
    .replace('maxresdefault', 'mqdefault')
    .replace('sddefault', 'mqdefault')
    .replace('hqdefault', 'mqdefault');
}

export const PodcastCard = ({ podcast }: PodcastCardProps) => {
  const navigate = useNavigate();
  const [thumbnailUrls, setThumbnailUrls] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const loadThumbnails = async () => {
      try {
        // First, try to get latest episode thumbnails
        const episodes = await getLatestEpisodesByPodcastId(podcast.id, MAX_EPISODES);
        const episodeThumbnails: string[] = [];

        for (const episode of episodes) {
          if (episode.thumbnail_url) {
            // Convert to medium quality for better performance
            const mediumUrl = convertToMediumThumbnail(episode.thumbnail_url);
            episodeThumbnails.push(mediumUrl);
          }
        }

        // If we have episode thumbnails, use them
        if (episodeThumbnails.length > 0) {
          setThumbnailUrls(episodeThumbnails);
          return;
        }

        // Fallback: try static images
        const staticUrls: string[] = [];
        for (let i = 1; i <= STATIC_IMAGE_COUNT; i++) {
          const imagePath = `/assets/podcasts/${podcast.id}/${i}.jpg`;
          try {
            const response = await fetch(imagePath, { method: 'HEAD' });
            if (response.ok) {
              staticUrls.push(imagePath);
            }
          } catch (error) {
            console.log(`Static image ${i} not found for podcast ${podcast.id}`, error);
          }
        }

        setThumbnailUrls(staticUrls);
      } catch (error) {
        console.error(`Failed to load thumbnails for podcast ${podcast.id}:`, error);
        // On error, try to load static images as fallback
        const staticUrls: string[] = [];
        for (let i = 1; i <= STATIC_IMAGE_COUNT; i++) {
          const imagePath = `/assets/podcasts/${podcast.id}/${i}.jpg`;
          try {
            const response = await fetch(imagePath, { method: 'HEAD' });
            if (response.ok) {
              staticUrls.push(imagePath);
            }
          } catch {
            // Ignore errors
          }
        }
        setThumbnailUrls(staticUrls);
      }
    };

    loadThumbnails();
  }, [podcast.id]);

  // Rotate thumbnails
  useEffect(() => {
    if (thumbnailUrls.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex(current => (current + 1) % thumbnailUrls.length);
    }, ROTATION_INTERVAL);

    return () => clearInterval(interval);
  }, [thumbnailUrls.length]);

  const handleClick = () => {
    // Use the custom name if available, otherwise fall back to ID
    const slug = slugify(podcast.title);
    navigate(`/podcasts/${podcast.id}-${slug}`);
  };

  return (
    <Card className="group h-full overflow-hidden bg-white/10 hover:bg-white/20 transition-all duration-300 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl">
      <div
        className="relative aspect-video cursor-pointer overflow-hidden"
        onClick={handleClick}
      >
        {thumbnailUrls.length > 0 ? (
          <img
            src={thumbnailUrls[currentIndex]}
            alt={podcast.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={(e) => {
              // When an image fails to load, remove it from thumbnailUrls so it won't be shown
              const target = e.target as HTMLImageElement;
              setThumbnailUrls(prevUrls => prevUrls.filter(url => url !== target.src));
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-purple-500/50 to-blue-500/50 flex items-center justify-center">
            <span className="text-4xl text-white/80">🎙️</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      
      <CardContent className="p-4">
        <h3 className="text-lg font-averia-bold text-black mb-2 line-clamp-1">{podcast.title}</h3>
        <p className="text-sm text-gray-300 mb-3 line-clamp-2">{podcast.description}</p>
        <div className="flex items-center justify-between text-xs text-gray-400">
         
        </div>
      </CardContent>
    </Card>
  );
}; 
